import { useCallback, useEffect, useState } from 'react';
import { ApiError, getFolders, type FolderSummary, type Space } from '@/lib/api';

/**
 * One shared space and its folders (FIL-2).
 *
 * Keyed by space id and refetched when it changes, because the space root is a
 * URL (ADR-0007): navigating from one space to another remounts nothing, and a
 * hook that only fetched on mount would show the previous counterpart's folders
 * under the new counterpart's name.
 *
 * **This is not authorization.** SEC-5 puts that in the API. `space.canWrite`
 * decides what the UI offers; the API decides what happens.
 */

export type FoldersState =
  | { kind: 'loading' }
  /** An empty folder list is a loaded state — a space nobody has used yet. */
  | { kind: 'loaded'; space: Space; folders: FolderSummary[] }
  /** `status` carried for the same reason as in `useSpaces`. `0` is network-level. */
  | { kind: 'error'; status: number };

export interface UseFoldersResult {
  state: FoldersState;
  /** After a failure. Clears back to `loading` first. */
  retry: () => void;
  /**
   * After a mutation — creating a folder.
   *
   * Deliberately does **not** reset to `loading`: the cards on screen are still
   * true while the refetch is in flight.
   */
  refresh: () => void;
}

export function useFolders(spaceId: string): UseFoldersResult {
  const [state, setState] = useState<FoldersState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    getFolders(spaceId, controller.signal)
      .then(({ space, folders }) => {
        setState({ kind: 'loaded', space, folders });
      })
      .catch((error: unknown) => {
        // An abort is our own doing, not a failure.
        if (error instanceof Error && error.name === 'AbortError') return;
        setState({ kind: 'error', status: error instanceof ApiError ? error.status : 0 });
      });

    return () => {
      controller.abort();
    };
  }, [spaceId, reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((current) => current + 1);
  }, []);

  // The loading reset belongs in the handler, not the effect: calling setState
  // in an effect body triggers a cascading render.
  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((current) => current + 1);
  }, []);

  return { state, retry, refresh };
}
